import {
  CreateSessionRequest,
  CreateSessionResponse,
  SendMessageRequest,
  SendMessageResponse,
  ChatHistoryRequest,
  ChatHistoryResponse
} from '@/types/aiChat';

const AI_CHAT_PREFIX = '/api/v1/ai-chat';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// AI聊天专用的API客户端，处理AI聊天接口的特殊响应格式
class AIChatApiClient {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // 默认配置
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // AI聊天使用session_token，不使用auth_token
    // 只有管理员接口才需要auth_token认证
    if (endpoint.includes('/admin/')) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    } else {
      // 对于普通AI聊天接口，使用session_token（如果存在）
      const sessionData = localStorage.getItem('ai_chat_session');
      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          if (session.session_token) {
            config.headers = {
              ...config.headers,
              'X-Session-Token': session.session_token,
            };
          }
        } catch (error) {
          console.warn('解析AI聊天会话数据失败:', error);
        }
      }
    }

    console.log('AI Chat API request:', { url, method: config.method || 'GET' });

    try {
      const response = await fetch(url, config);
      console.log('AI Chat API response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('AI Chat API response data:', data);

      // AI聊天接口使用两种响应格式：
      // 1. 创建会话: {success: boolean, data: T, message: string}
      // 2. 发送消息: {code: number, data: T, message: string}

      // 检查第一种格式（success字段）
      if (data.hasOwnProperty('success')) {
        if (data.success === false) {
          throw new Error(data.message || '请求失败');
        }
        return data.data || data;
      }

      // 检查第二种格式（code字段）
      if (data.hasOwnProperty('code')) {
        if (data.code !== 200) {
          throw new Error(data.message || '请求失败');
        }
        return data.data || data;
      }

      // 如果都不匹配，直接返回数据
      return data;
    } catch (error) {
      console.error('AI Chat API request error:', error);
      throw error;
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
    });
  }
}

const aiChatApiClient = new AIChatApiClient();

// AI聊天API服务
export const aiChatApi = {
  // 创建聊天会话
  createSession: async (data: CreateSessionRequest): Promise<CreateSessionResponse> => {
    return aiChatApiClient.post(`${AI_CHAT_PREFIX}/sessions`, data);
  },

  // 发送消息
  sendMessage: async (data: SendMessageRequest): Promise<SendMessageResponse> => {
    return aiChatApiClient.post(`${AI_CHAT_PREFIX}/messages`, data);
  },

  // 发送消息（流式）
  sendMessageStream: (data: SendMessageRequest, callbacks: {
    onContent?: (content: string) => void;
    onStats?: (stats: any) => void;
    onError?: (error: string) => void;
    onComplete?: () => void;
  }): Promise<void> => {
    const url = `${API_BASE_URL}${AI_CHAT_PREFIX}/messages/stream`;

    // 构建请求配置
    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // 使用session_token（如果存在）
        ...(localStorage.getItem('ai_chat_session') ? {
          'X-Session-Token': JSON.parse(localStorage.getItem('ai_chat_session')!).session_token
        } : {})
      },
      body: JSON.stringify(data)
    };

    return new Promise((resolve, reject) => {
      // 发送请求
      fetch(url, config)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('无法获取响应流');
          }

          const decoder = new TextDecoder();
          let buffer = '';

          // 读取流式数据
          function readStream(): Promise<void> {
            return reader!.read().then(({ done, value }) => {
              if (done) {
                if (callbacks.onComplete) callbacks.onComplete();
                resolve();
                return;
              }

              // 解码数据并处理
              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;

              // 处理SSE数据
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // 保留最后一行（可能不完整）

              let currentEvent = '';
              let currentData = '';

              for (const line of lines) {
                if (line.trim() === '') continue;
                if (line.startsWith(':')) continue; // SSE注释

                if (line.startsWith('event:')) {
                  currentEvent = line.slice(6).trim();
                } else if (line.startsWith('data:')) {
                  currentData = line.slice(5).trim();
                }

                // 当我们收集到event和data时处理它们
                if (currentEvent && currentData) {
                  try {
                    let parsedData;
                    // 尝试解析JSON，如果失败则作为字符串处理
                    try {
                      parsedData = currentData ? JSON.parse(currentData) : {};
                    } catch {
                      parsedData = currentData; // 作为字符串处理
                    }

                    switch (currentEvent) {
                      case 'content':
                        if (callbacks.onContent && parsedData) {
                          // 如果是字符串，直接传递；如果是对象，提取content字段
                          const content = typeof parsedData === 'string' ? parsedData : (parsedData.content || parsedData);
                          callbacks.onContent(content);
                        }
                        break;
                      case 'stats':
                        if (callbacks.onStats && parsedData) {
                          callbacks.onStats(parsedData);
                        }
                        break;
                      case 'error':
                        if (callbacks.onError) {
                          const errorMsg = typeof parsedData === 'string' ? parsedData : (parsedData.message || parsedData);
                          callbacks.onError(errorMsg);
                        }
                        break;
                      case 'done':
                        if (callbacks.onComplete) {
                          callbacks.onComplete();
                        }
                        return;
                    }
                  } catch (e) {
                    console.warn('解析SSE数据失败:', e, 'Event:', currentEvent, 'Data:', currentData);
                  }

                  // 重置当前事件和数据
                  currentEvent = '';
                  currentData = '';
                }
              }

              return readStream();
            });
          }

          return readStream();
        })
        .catch(error => {
          console.error('流式请求失败:', error);
          if (callbacks.onError) {
            callbacks.onError(error.message);
          }
          reject(error);
        });
    });
  },

  // 获取聊天历史
  getChatHistory: async (params: ChatHistoryRequest): Promise<ChatHistoryResponse> => {
    const { session_id, page = 1, size = 20 } = params;
    return aiChatApiClient.get(`${AI_CHAT_PREFIX}/sessions/${session_id}/messages?page=${page}&size=${size}`);
  },

  // 关闭会话
  closeSession: async (sessionId: string): Promise<{ message: string }> => {
    return aiChatApiClient.post(`${AI_CHAT_PREFIX}/sessions/${sessionId}/close`);
  },

  // 管理员接口 - 获取提供商统计
  getProviderStats: async (): Promise<any> => {
    return aiChatApiClient.get(`${AI_CHAT_PREFIX}/admin/providers/stats`);
  },

  // 管理员接口 - 重新加载提供商配置
  reloadProviders: async (): Promise<{ message: string }> => {
    return aiChatApiClient.post(`${AI_CHAT_PREFIX}/admin/providers/reload`);
  },

  // 管理员接口 - 获取活跃配置
  getActiveConfigs: async (): Promise<any> => {
    return aiChatApiClient.get(`${AI_CHAT_PREFIX}/admin/configs`);
  }
};

// AI聊天会话管理工具
export const aiChatSessionManager = {
  // 存储会话信息到localStorage
  saveSession: (session: CreateSessionResponse) => {
    localStorage.setItem('ai_chat_session', JSON.stringify({
      session_id: session.session_id,
      session_token: session.session_token,
      expires_at: session.expires_at,
      created_at: new Date().toISOString()
    }));
  },

  // 从localStorage获取会话信息
  getSession: (): { session_id: string; session_token: string; expires_at: string; created_at: string } | null => {
    const sessionData = localStorage.getItem('ai_chat_session');
    if (!sessionData) return null;

    try {
      const session = JSON.parse(sessionData);
      // 检查会话是否过期
      if (new Date(session.expires_at) < new Date()) {
        localStorage.removeItem('ai_chat_session');
        return null;
      }
      return session;
    } catch {
      localStorage.removeItem('ai_chat_session');
      return null;
    }
  },

  // 清除会话信息
  clearSession: () => {
    localStorage.removeItem('ai_chat_session');
    localStorage.removeItem('ai_chat_messages');
  },

  // 存储聊天记录到localStorage
  saveMessages: (messages: SendMessageResponse[]) => {
    localStorage.setItem('ai_chat_messages', JSON.stringify(messages));
  },

  // 从localStorage获取聊天记录
  getMessages: (): SendMessageResponse[] => {
    const messagesData = localStorage.getItem('ai_chat_messages');
    if (!messagesData) return [];

    try {
      return JSON.parse(messagesData);
    } catch {
      return [];
    }
  },

  // 添加新消息到本地存储
  addMessage: (message: SendMessageResponse) => {
    const messages = aiChatSessionManager.getMessages();
    messages.push(message);
    // 限制存储消息数量（最多100条）
    if (messages.length > 100) {
      messages.splice(0, messages.length - 100);
    }
    aiChatSessionManager.saveMessages(messages);
  }
};

// 默认配置类型
interface AIChatConfigType {
  maxMessages: number;
  sessionTimeout: number;
  autoMinimizeDelay: number;
  enableNotifications: boolean;
  enableSound: boolean;
  theme: 'light' | 'dark' | 'auto';
}

// 默认配置值
const defaultConfig: AIChatConfigType = {
  maxMessages: 50,
  sessionTimeout: 24 * 60 * 60 * 1000, // 24小时
  autoMinimizeDelay: 30000, // 30秒
  enableNotifications: true,
  enableSound: false,
  theme: 'auto'
};

// AI聊天配置管理
export const aiChatConfigManager = {
  // 默认配置
  defaultConfig,

  // 保存配置
  saveConfig: (config: Partial<AIChatConfigType>) => {
    const currentConfig = aiChatConfigManager.getConfig();
    const newConfig = { ...currentConfig, ...config };
    localStorage.setItem('ai_chat_config', JSON.stringify(newConfig));
  },

  // 获取配置
  getConfig: (): AIChatConfigType => {
    const configData = localStorage.getItem('ai_chat_config');
    if (!configData) return defaultConfig;

    try {
      return { ...defaultConfig, ...JSON.parse(configData) };
    } catch {
      return defaultConfig;
    }
  }
};